import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Camera, UserCog } from "lucide-react";

const ISSUE_CATEGORIES = ["plumbing", "electrical", "security", "housekeeping", "parking", "water", "lift", "noise", "vendor", "admin", "suggestion", "other"];

export default function SettingsPage() {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/communities/mine").then((r) => setC(r.data));
    if (user?.community_id) {
      api.get(`/communities/${user.community_id}/users`).then((r) => setMembers(r.data));
    }
  }, [user?.community_id]);

  async function save(e) {
    e.preventDefault();
    try {
      await api.patch(`/communities/${user.community_id}`, {
        name: c.name, address: c.address, city: c.city,
        logo_url: c.logo_url, drive_link: c.drive_link, issue_prefix: c.issue_prefix,
        default_assignees: c.default_assignees || {},
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
      setC((prev) => ({ ...prev, logo_url: url }));
      toast.success("Logo uploaded — remember to save");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function setAssignee(cat, uid) {
    setC((prev) => ({ ...prev, default_assignees: { ...(prev.default_assignees || {}), [cat]: uid || null } }));
  }

  if (!c) return <div className="p-10 text-stone-500">Loading…</div>;
  if (user?.role !== "committee") return <div className="p-10 text-stone-500">Committee-only page.</div>;

  const da = c.default_assignees || {};

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-2xl">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Settings</div>
      <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Apartment settings</h1>

      <form onSubmit={save} className="mt-7 bg-white border border-stone-200 rounded-2xl p-6 space-y-4" data-testid="settings-form">
        <h2 className="font-serif-display text-xl font-medium">Branding</h2>
        <div className="flex items-center gap-4">
          {c.logo_url ? (
            <img src={c.logo_url} alt={c.name} className="w-16 h-16 rounded-xl object-cover border border-stone-200" data-testid="logo-preview" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-[#C05640] grid place-items-center text-white text-2xl font-serif-display">{c.name?.[0] || "N"}</div>
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm hover:border-stone-500" data-testid="upload-logo">
            <Camera size={16} /> Upload logo / nameplate photo
            <input type="file" accept="image/*" hidden onChange={uploadLogo} />
          </label>
        </div>
        <div><Label>Apartment name</Label><Input value={c.name || ""} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="st-name" className="h-11 mt-1.5" /></div>
        <div><Label>Address</Label><Input value={c.address || ""} onChange={(e) => setC({ ...c, address: e.target.value })} data-testid="st-addr" className="h-11 mt-1.5" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>City</Label><Input value={c.city || ""} onChange={(e) => setC({ ...c, city: e.target.value })} data-testid="st-city" className="h-11 mt-1.5" /></div>
          <div><Label>Issue prefix</Label><Input value={c.issue_prefix || ""} onChange={(e) => setC({ ...c, issue_prefix: e.target.value.toUpperCase().slice(0, 5) })} data-testid="st-prefix" className="h-11 mt-1.5 font-mono" /></div>
        </div>

        <h2 className="font-serif-display text-xl font-medium pt-3 inline-flex items-center gap-2"><UserCog size={18} /> Default assignee per category</h2>
        <p className="text-xs text-stone-500">When a resident files an issue, it can auto-assign to the right committee member. Leave blank for none.</p>
        <div className="space-y-2" data-testid="default-assignees">
          <div className="grid grid-cols-2 gap-2 items-center">
            <Label className="text-xs">Default (any category)</Label>
            <select value={da._default || ""} onChange={(e) => setAssignee("_default", e.target.value)}
              data-testid="da-_default"
              className="h-9 rounded-lg border border-stone-300 px-2 bg-white text-sm">
              <option value="">— None —</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
          </div>
          {ISSUE_CATEGORIES.map((cat) => (
            <div key={cat} className="grid grid-cols-2 gap-2 items-center">
              <Label className="text-xs capitalize">{cat}</Label>
              <select value={da[cat] || ""} onChange={(e) => setAssignee(cat, e.target.value)}
                data-testid={`da-${cat}`}
                className="h-9 rounded-lg border border-stone-300 px-2 bg-white text-sm">
                <option value="">— Use default —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
          ))}
        </div>

        <h2 className="font-serif-display text-xl font-medium pt-3">Document overflow</h2>
        <p className="text-xs text-stone-500">Built-in storage has a small cap. Share a community Google Drive / OneDrive link so residents can access bigger files.</p>
        <div><Label>Shared Drive link</Label><Input value={c.drive_link || ""} onChange={(e) => setC({ ...c, drive_link: e.target.value })} data-testid="st-drive" placeholder="https://drive.google.com/drive/folders/…" className="h-11 mt-1.5" /></div>

        <Button type="submit" data-testid="st-save" className="rounded-full bg-[#C05640] hover:bg-[#A54734]">Save settings</Button>
      </form>
    </div>
  );
}
